import { useEffect, useState } from "react";
import { api, formatErr, getFileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Upload, Plus, Eye } from "lucide-react";
import { toast } from "sonner";

export default function EmployeeBills() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ item_name: "", amount: "", date: new Date().toISOString().slice(0,10), bill_file_path: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/reimbursements").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, bill_file_path: data.path }));
      toast.success("Bill uploaded");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/reimbursements", { ...form, amount: Number(form.amount) });
      toast.success("Submitted");
      setOpen(false); setForm({ item_name: "", amount: "", date: new Date().toISOString().slice(0,10), bill_file_path: "" });
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const badge = (s) => ({
    pending: "bg-amber-50 text-amber-700 border-amber-300",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
    rejected: "bg-red-50 text-red-700 border-red-300",
  }[s] || "bg-zinc-100 dark:bg-zinc-800");

  return (
    <div className="space-y-6" data-testid="employee-bills-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">02 / Reimbursements</div>
          <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">My Bills.</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-11 px-6" data-testid="submit-bill-btn">
              <Plus size={14} className="mr-2" /> Submit Bill
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl">
            <DialogHeader><DialogTitle className="rule-text text-2xl">New Reimbursement</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="eyebrow">Item Name</Label>
                <Input value={form.item_name} onChange={(e) => setForm({...form, item_name: e.target.value})} required className="rounded-xl mt-1" data-testid="bill-item-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="eyebrow">Amount (₹)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} required className="rounded-xl mt-1" data-testid="bill-amount-input" />
                </div>
                <div>
                  <Label className="eyebrow">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required className="rounded-xl mt-1" data-testid="bill-date-input" />
                </div>
              </div>
              <div>
                <Label className="eyebrow">Bill (Image / PDF)</Label>
                <label className="mt-1 flex items-center gap-2 border border-zinc-300 px-3 h-10 cursor-pointer hover:bg-zinc-50 text-sm">
                  <Upload size={14} />
                  {form.bill_file_path ? <span className="text-emerald-600 text-xs">Uploaded ✓</span> : <span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Choose file...</span>}
                  <input type="file" className="hidden" accept="image/*,application/pdf" onChange={upload} data-testid="bill-file-input" />
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold" data-testid="submit-bill-confirm">
                  {saving ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm overflow-x-auto">
          <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Item</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Amount</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Date</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Bill</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-zinc-500 dark:text-zinc-400 text-sm">No bills submitted yet.</TableCell></TableRow>
            ) : list.map((r) => (
              <TableRow key={r.id} data-testid={`bill-row-${r.id}`}>
                <TableCell className="font-bold">{r.item_name}</TableCell>
                <TableCell className="font-mono">₹ {r.amount.toFixed(2)}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell>
                  {r.bill_file_path ? (
                    <a href={getFileUrl(r.bill_file_path)} target="_blank" rel="noreferrer" className="text-[#4F46E5] hover:underline inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em]"><Eye size={12} /> View</a>
                  ) : <span className="text-zinc-400 text-xs">—</span>}
                </TableCell>
                <TableCell><span className={`eyebrow px-2 py-1 border ${badge(r.status)}`}>{r.status}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
